import type { Option, SourcedOption } from '@/modules/common/types/option.type.ts';

type GetListFn<T> = (signal: AbortSignal) => Promise<T[]>;
type GetLabelFn<T> = (response: T) => string;
type GetCodeFn<T> = (response: T) => string | number;

type OptionsLoaderConfig<T> = {
  getCode?: GetCodeFn<T>;
  sort?: boolean;
};

export class OptionsUtil {
  static sortByLabel<T extends Option>(options: T[]): T[] {
    return [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }

  static createOptionsLoader<T>(
    getList: GetListFn<T>,
    getLabel: GetLabelFn<T>,
    { getCode = getLabel, sort = false }: OptionsLoaderConfig<T> = {},
  ) {
    return async (signal: AbortSignal) => {
      const options = (await getList(signal)).map(
        (item): Option => ({
          label: getLabel(item),
          code: getCode(item),
        }),
      );

      return sort ? OptionsUtil.sortByLabel(options) : options;
    };
  }

  static createSortedOptionsLoader<T>(
    getList: GetListFn<T>,
    getLabel: GetLabelFn<T>,
    getCode: GetCodeFn<T> = getLabel,
  ) {
    return OptionsUtil.createOptionsLoader(getList, getLabel, { getCode, sort: true });
  }

  static createSourcedOptionsLoader<T>(
    getList: GetListFn<T>,
    getLabel: GetLabelFn<T>,
    { getCode = getLabel, sort = false }: OptionsLoaderConfig<T> = {},
  ) {
    return async (signal: AbortSignal) => {
      const options = (await getList(signal)).map(
        (item): SourcedOption<T> => ({
          label: getLabel(item),
          code: getCode(item),
          source: item,
        }),
      );

      return sort ? OptionsUtil.sortByLabel(options) : options;
    };
  }

  static createSortedSourcedOptionsLoader<T>(
    getList: GetListFn<T>,
    getLabel: GetLabelFn<T>,
    getCode: GetCodeFn<T> = getLabel,
  ) {
    return OptionsUtil.createSourcedOptionsLoader(getList, getLabel, { getCode, sort: true });
  }
}
